// FE-SCRM-052 — URL-codec for the Opportunities list view.
//
// The URL is the canonical state for filters + sort + search +
// pagination so a deep link / back-button restores the exact view.
// Each filter has a stable key and CSV-encoded multi-value where
// applicable. Two empty filters round-trip to two empty URLSearchParams.

import {
  EMPTY_FILTERS,
  type CadenceHealth,
  type LivesBand,
  type LostReason,
  type OpportunityListFilters,
  type RenewalConfidence,
} from "./types";


const CSV_KEYS: Array<keyof OpportunityListFilters> = [
  "stage",
  "opportunity_type",
  "producer",
  "broker_location",
  "lives_band",
  "renewal_confidence",
  "cadence_health",
  "lost_reason",
];


const LIVES_BANDS: LivesBand[] = [
  "micro", "small", "mid_market", "large", "enterprise",
];
const RENEWAL_CONFIDENCES: RenewalConfidence[] = [
  "confirmed", "inferred_from_form5500", "producer_estimate", "unknown",
];
const CADENCE_HEALTHS: CadenceHealth[] = [
  "ahead", "on_track", "behind", "critical", "not_yet_urgent",
];
const LOST_REASONS: LostReason[] = [
  "incumbent_retained", "lost_to_named_broker", "price", "no_decision",
  "account_dissolved", "account_acquired_by_parent",
  "service_concern", "relationship_concern", "compliance_concern",
  "coverage_gap_concern", "other",
];


function readCsv(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key);
  if (!raw) return [];
  return raw.split(",").map((v) => v.trim()).filter(Boolean);
}


function readEnumCsv<T extends string>(
  params: URLSearchParams, key: string, allowed: readonly T[],
): T[] {
  const allowedSet = new Set(allowed as readonly string[]);
  return readCsv(params, key).filter((v) => allowedSet.has(v)) as T[];
}


export function readFiltersFromUrl(
  params: URLSearchParams,
): OpportunityListFilters {
  return {
    stage: readCsv(params, "stage"),
    opportunity_type: readCsv(params, "opportunity_type"),
    producer: readCsv(params, "producer"),
    broker_location: readCsv(params, "broker_location"),
    lives_band: readEnumCsv(params, "lives_band", LIVES_BANDS),
    renewal_confidence: readEnumCsv(
      params, "renewal_confidence", RENEWAL_CONFIDENCES,
    ),
    cadence_health: readEnumCsv(params, "cadence_health", CADENCE_HEALTHS),
    lost_reason: readEnumCsv(params, "lost_reason", LOST_REASONS),
    alerts_only: params.get("alerts_only") === "true",
    closed_at_after: params.get("closed_at_after") || null,
    closed_at_before: params.get("closed_at_before") || null,
  };
}


export function writeFiltersToParams(
  filters: OpportunityListFilters,
  base: URLSearchParams,
): URLSearchParams {
  const out = new URLSearchParams(base);
  for (const k of CSV_KEYS) {
    out.delete(k);
    const v = filters[k] as readonly string[];
    if (v.length) out.set(k, v.join(","));
  }
  out.delete("alerts_only");
  if (filters.alerts_only) out.set("alerts_only", "true");
  out.delete("closed_at_after");
  if (filters.closed_at_after) {
    out.set("closed_at_after", filters.closed_at_after);
  }
  out.delete("closed_at_before");
  if (filters.closed_at_before) {
    out.set("closed_at_before", filters.closed_at_before);
  }
  return out;
}


export const SORT_KEY = "ordering";
export const DEFAULT_ORDERING = "-created_at";


export function readOrderingFromUrl(params: URLSearchParams): string {
  return (params.get(SORT_KEY) || DEFAULT_ORDERING).trim();
}


export function writeOrderingToParams(
  ordering: string, base: URLSearchParams,
): URLSearchParams {
  const out = new URLSearchParams(base);
  out.delete(SORT_KEY);
  if (ordering && ordering !== DEFAULT_ORDERING) {
    out.set(SORT_KEY, ordering);
  }
  return out;
}


/** Compose filters + sort + search + pagination + archived into the
 * ``URLSearchParams`` shape the BE endpoint expects. The FE's
 * URL-state shape mirrors the BE one-for-one, so this is a thin
 * mapping that mostly forwards values. */
export function buildApiQuery(input: {
  filters: OpportunityListFilters;
  ordering: string;
  q: string;
  page: number;
  pageSize: number;
  archived: boolean;
}): URLSearchParams {
  const out = new URLSearchParams();
  for (const k of CSV_KEYS) {
    const v = input.filters[k] as readonly string[];
    if (v.length) out.set(k, v.join(","));
  }
  if (input.filters.alerts_only) out.set("alerts_only", "true");
  if (input.filters.closed_at_after) {
    out.set("closed_at_after", input.filters.closed_at_after);
  }
  if (input.filters.closed_at_before) {
    out.set("closed_at_before", input.filters.closed_at_before);
  }
  if (input.q) out.set("q", input.q);
  if (input.ordering && input.ordering !== DEFAULT_ORDERING) {
    out.set("ordering", input.ordering);
  }
  if (input.page > 1) out.set("page", String(input.page));
  out.set("page_size", String(input.pageSize));
  if (input.archived) out.set("archived", "on");
  return out;
}


export function filtersEqual(
  a: OpportunityListFilters, b: OpportunityListFilters,
): boolean {
  if (a.alerts_only !== b.alerts_only) return false;
  if (a.closed_at_after !== b.closed_at_after) return false;
  if (a.closed_at_before !== b.closed_at_before) return false;
  for (const k of CSV_KEYS) {
    const av = a[k] as readonly string[];
    const bv = b[k] as readonly string[];
    if (av.length !== bv.length) return false;
    for (let i = 0; i < av.length; i++) {
      if (av[i] !== bv[i]) return false;
    }
  }
  return true;
}


export function hasActiveFilters(filters: OpportunityListFilters): boolean {
  return !filtersEqual(filters, EMPTY_FILTERS);
}
